import type { ApiDashboardElement } from '@highimpact-frontend/api-types';
import type { Layout, LayoutAxis } from 'plotly.js-dist-min';

import { groupElementYAxes } from './buildElementPlotMetadata';
import { getConfiguredPlotType } from './elementMeasureSupport';

export interface ElementPlotAxisLayoutEntry {
  readonly key: string;
  readonly unit: string;
  readonly layout: Partial<LayoutAxis>;
}

/** Fixed Y-axis title for ombrothermic precipitation. */
export const OMBROTHERMIC_PRECIPITATION_AXIS_TITLE = 'Pluviométrie (mm/jour)';

/** Fixed Y-axis title for ombrothermic temperature. */
export const OMBROTHERMIC_TEMPERATURE_AXIS_TITLE = 'Température (°C)';

/** Plotly default color scheme (blue, orange) for ombrothermic traces and axes. */
export const OMBROTHERMIC_PLOT_COLORWAY = ['#1f77b4', '#ff7f0e'] as const;

export const OMBROTHERMIC_PRECIPITATION_TRACE_COLOR =
  OMBROTHERMIC_PLOT_COLORWAY[0];

export const OMBROTHERMIC_TEMPERATURE_TRACE_COLOR = OMBROTHERMIC_PLOT_COLORWAY[1];

function buildStyledOmbrothermicYAxis(
  title: string,
  color: string,
  extra?: Partial<LayoutAxis>
): Partial<LayoutAxis> {
  return {
    title: { text: title, font: { color } },
    tickfont: { color },
    linecolor: color,
    tickcolor: color,
    zeroline: false,
    automargin: true,
    autorange: true,
    ...extra,
  };
}

/** Fixed polar layout for wind rose widgets (data_source_to_axis ignored). */
export function buildWindrosePlotlyLayout(): Partial<Layout> {
  return {
    polar: {
      radialaxis: { angle: 90 },
      angularaxis: { direction: 'clockwise' },
    },
    margin: {
      t: 32,
      l: 32,
      r: 32,
      pad: 8,
    },
    legend: {
      xanchor: 'left',
      yanchor: 'bottom',
      x: 0,
      y: 0,
      orientation: 'v',
    },
  };
}

function buildAutorangeYAxisLayout(
  groups: ReturnType<typeof groupElementYAxes>,
  options?: { overlaySecondOnFirst?: boolean }
): Record<string, Partial<LayoutAxis>> {
  const layout: Record<string, Partial<LayoutAxis>> = {};

  groups.forEach((group, index) => {
    layout[group.axisKey] = {
      title: { text: group.axisName },
      zeroline: false,
      automargin: true,
      autorange: true,
      ...(index === 1 && options?.overlaySecondOnFirst && groups[0]
        ? { overlaying: 'y' as LayoutAxis['overlaying'], side: 'right' }
        : {}),
    };
  });

  return layout;
}

/** Bar/histogram Plotly axis layout from element JSON (autorange, no persisted ranges). */
export function buildHistogramPlotlyLayoutFromElement(
  element: ApiDashboardElement
): Record<string, Partial<LayoutAxis>> {
  const yGroups = groupElementYAxes(element);
  const layout = buildAutorangeYAxisLayout(yGroups);

  layout.xaxis = {
    automargin: true,
    autorange: true,
  };

  return layout;
}

/** Ombrothermic Plotly axis layout with fixed axis titles and trace colors. */
export function buildOmbrothermicPlotlyLayoutFromElement(
  _element: ApiDashboardElement
): Partial<Layout> {
  return {
    colorway: [...OMBROTHERMIC_PLOT_COLORWAY],
    yaxis: buildStyledOmbrothermicYAxis(
      OMBROTHERMIC_PRECIPITATION_AXIS_TITLE,
      OMBROTHERMIC_PRECIPITATION_TRACE_COLOR
    ),
    yaxis2: buildStyledOmbrothermicYAxis(
      OMBROTHERMIC_TEMPERATURE_AXIS_TITLE,
      OMBROTHERMIC_TEMPERATURE_TRACE_COLOR,
      { overlaying: 'y', side: 'right', showgrid: false }
    ),
    xaxis: {
      type: 'date',
      tickformat: '%d/%m/%y',
      automargin: true,
      autorange: true,
    },
    xaxis2: {
      overlaying: 'x',
      side: 'top',
      showgrid: false,
      automargin: true,
      autorange: true,
    },
  };
}

/** Plotly partial layout derived from configured plot element JSON. */
export function buildPlotlyLayoutFromElement(
  element: ApiDashboardElement
): Partial<Layout> {
  const plotType = getConfiguredPlotType(element);

  switch (plotType) {
    case 'windrose':
      return buildWindrosePlotlyLayout();
    case 'bar':
      return buildHistogramPlotlyLayoutFromElement(element);
    case 'ombrothermic':
      return buildOmbrothermicPlotlyLayoutFromElement(element);
    default:
      return {};
  }
}

/** Axis layout entries keyed by Plotly axis id for histogram hover units. */
export function buildHistogramAxisConfigFromElement(
  element: ApiDashboardElement
): Record<number, ElementPlotAxisLayoutEntry> {
  const groups = groupElementYAxes(element);

  return Object.fromEntries(
    groups.map((group, index) => [
      index,
      {
        key: group.axisKey,
        unit: group.unit,
        layout: {
          title: { text: group.axisName },
        },
      },
    ])
  );
}
